import { useSearchParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MapPin, Phone, Building, User, Printer } from "lucide-react";
import { getObraById, type ObraRecord } from "@/lib/obraCache";
import { getClienteNomeById } from "@/utils/clientesData";

const NAO_INFORMADO = "Não informado";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("pt-BR") : NAO_INFORMADO;

// Monta os dados de exportação a partir da obra real (obraCache/Supabase).
// Campos sem correspondência no cadastro ficam como "Não informado"; fases e
// equipe não existem no modelo atual e por isso as seções ficam ocultas.
const buildWorkData = (obra: ObraRecord) => {
  const endDate = obra.previsaoTermino ? new Date(obra.previsaoTermino) : null;
  const daysRemaining = endDate
    ? Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / 86400000))
    : 0;

  const enderecoPartes = [
    [obra.endereco?.rua, obra.endereco?.numero].filter(Boolean).join(", "),
    obra.endereco?.cidade,
    obra.endereco?.estado,
  ].filter(Boolean);

  return {
    id: obra.id,
    name: obra.nomeObra || "Obra sem nome",
    client: obra.clienteObra ? getClienteNomeById(obra.clienteObra) : NAO_INFORMADO,
    status: (obra.statusObra === "pausado" || obra.statusObra === "cancelado"
      ? "critical"
      : "active") as "active" | "critical",
    progress: 0,
    daysRemaining,
    startDate: formatDate(obra.dataInicio as string | undefined),
    endDate: formatDate(obra.previsaoTermino as string | undefined),
    budget: NAO_INFORMADO,
    area: NAO_INFORMADO,
    address: enderecoPartes.length ? enderecoPartes.join(" - ") : NAO_INFORMADO,
    cep: obra.endereco?.cep || NAO_INFORMADO,
    engineer: obra.responsaveisObra?.[0] ?? NAO_INFORMADO,
    crea: NAO_INFORMADO,
    contractor: NAO_INFORMADO,
    cnpj: NAO_INFORMADO,
    description: obra.endereco?.observacao || "",
    phases: [] as { name: string; progress: number; status: string }[],
    team: [] as { name: string; role: string; phone: string; email: string }[],
  };
};

const statusConfig = {
  active: { label: "Em Andamento", variant: "default" as const },
  attention: { label: "Atenção", variant: "secondary" as const },
  critical: { label: "Crítico", variant: "destructive" as const }
};

const ExportObra = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const workId = searchParams.get('workId') ?? '';
  const sections = searchParams.get('sections')?.split(',') || [];
  const teamMembers = searchParams.get('teamMembers')?.split('|') || [];

  const obra = workId ? getObraById(workId) : undefined;

  if (!obra) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">
            Obra não encontrada{workId ? ` (id: ${workId})` : ""}.
          </p>
          <Button onClick={() => navigate("/obras")}>Ver minhas obras</Button>
        </div>
      </div>
    );
  }

  const workData = buildWorkData(obra);
  const statusInfo = statusConfig[workData.status];

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between print:hidden">
          <h1 className="text-3xl font-bold text-foreground">Relatório da Obra</h1>
          <Button onClick={handlePrint} className="gap-2">
            <Printer className="h-4 w-4" />
            Imprimir
          </Button>
        </div>

        {/* Work Info Card */}
        <div className="bg-card rounded-lg shadow-card p-6 space-y-6">
          
          {/* Basic Information */}
          {sections.includes('basicInfo') && (
            <div>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Building className="h-6 w-6" />
                {workData.name}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-muted-foreground">Cliente:</span>
                  <p className="font-medium">{workData.client}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <div className="mt-1">
                    <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Progresso:</span>
                  <p className="font-medium">{workData.progress}%</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Dias restantes:</span>
                  <p className="font-medium">{workData.daysRemaining} dias</p>
                </div>
              </div>
            </div>
          )}

          {sections.includes('basicInfo') && sections.length > 1 && <Separator />}

          {/* Schedule */}
          {sections.includes('schedule') && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Cronograma</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-muted-foreground">Data de início:</span>
                  <p className="font-medium">{workData.startDate}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Previsão de término:</span>
                  <p className="font-medium">{workData.endDate}</p>
                </div>
              </div>
            </div>
          )}

          {sections.includes('schedule') && (sections.includes('financial') || sections.includes('location') || sections.includes('description') || sections.includes('phases') || sections.includes('team')) && <Separator />}

          {/* Financial */}
          {sections.includes('financial') && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Financeiro e Técnico</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-muted-foreground">Orçamento:</span>
                  <p className="font-medium">{workData.budget}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Área:</span>
                  <p className="font-medium">{workData.area}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Engenheiro responsável:</span>
                  <p className="font-medium">{workData.engineer}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">CREA:</span>
                  <p className="font-medium">{workData.crea}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Contratante:</span>
                  <p className="font-medium">{workData.contractor}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">CNPJ:</span>
                  <p className="font-medium">{workData.cnpj}</p>
                </div>
              </div>
            </div>
          )}

          {sections.includes('financial') && (sections.includes('location') || sections.includes('description') || sections.includes('phases') || sections.includes('team')) && <Separator />}

          {/* Location */}
          {sections.includes('location') && (
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Localização
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-muted-foreground">Endereço:</span>
                  <p className="font-medium">{workData.address}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">CEP:</span>
                  <p className="font-medium">{workData.cep}</p>
                </div>
              </div>
            </div>
          )}

          {sections.includes('location') && (sections.includes('description') || sections.includes('phases') || sections.includes('team')) && <Separator />}

          {/* Description */}
          {sections.includes('description') && workData.description && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Descrição do Projeto</h3>
              <p className="text-muted-foreground leading-relaxed">{workData.description}</p>
            </div>
          )}

          {sections.includes('description') && (sections.includes('phases') || sections.includes('team')) && <Separator />}

          {/* Phases */}
          {sections.includes('phases') && workData.phases.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Fases do Projeto</h3>
              <div className="space-y-4">
                {workData.phases.map((phase, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{phase.name}</span>
                      <span className="text-sm text-muted-foreground">{phase.progress}%</span>
                    </div>
                    <Progress value={phase.progress} className="h-2" />
                    <div className="text-sm text-muted-foreground">{phase.status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sections.includes('phases') && sections.includes('team') && <Separator />}

          {/* Team */}
          {sections.includes('team') && workData.team.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <User className="h-5 w-5" />
                Equipe
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {workData.team
                  .filter(member => teamMembers.length === 0 || teamMembers.includes(member.name))
                  .map((member, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-2">
                    <div className="font-medium text-lg">{member.name}</div>
                    <div className="text-sm text-muted-foreground">
                      <div><strong>Telefone:</strong> {member.phone}</div>
                      <div><strong>E-mail:</strong> {member.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground print:block">
          <p>Relatório gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}</p>
        </div>
      </div>
    </div>
  );
};

export default ExportObra;